"use client"

import { Suspense, useState, useEffect } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { Eye, EyeOff, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AuthShell } from "@/components/auth/auth-shell"
import { login, getSession } from "@/lib/api/auth"
import { useUser } from "@/lib/context/user-context"
import { landingRoute } from "@/lib/access/route-access"
import { safeNext } from "@/lib/access/safe-redirect"

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  )
}

function LoginContent() {
  const router = useRouter()
  const { refetch } = useUser()
  const searchParams = useSearchParams()
  const showDemo = searchParams.get("help") === "true"
  // Where the session guard was taking this person before it stopped them —
  // the deep link they actually clicked. `null` when they came to /login on
  // their own, or when the value failed validation; either way we fall back to
  // the role's landing route below. Never read straight out of the URL: see
  // lib/access/safe-redirect.ts.
  const next = safeNext(searchParams.get("next"))
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  // Already logged in → redirect. Honours `next` too: a deep link followed in
  // a tab that already holds a session lands on the link, not the dashboard.
  useEffect(() => {
    const session = getSession()
    if (session) router.replace(next ?? landingRoute(session.global_roles, session.enabledFeatures))
  }, [router, next])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!email.trim() || !password.trim()) {
      setError("Please enter your email and password.")
      return
    }
    setIsLoading(true)
    try {
      const user = await login(email.trim(), password)
      await refetch()
      router.replace(next ?? landingRoute(user.global_roles, user.enabledFeatures))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed.")
      setIsLoading(false)
    }
  }

  return (
    <AuthShell>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">Sign in</h2>
        <p className="text-sm text-muted-foreground">
          Enter your work email and password to continue.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError("") }}
                disabled={isLoading}
                required
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  href="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError("") }}
                  disabled={isLoading}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2">
                <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          {/* Demo credentials hint */}
          {showDemo && <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Demo accounts</p>
            <div className="space-y-2">
              {[
                { email: "admin@dxtr.ai", label: "System Admin", role: "System Administrator", password: "admin1234" },
                { email: "parv@chemtech-cro.com", label: "Parv Kothari", role: "CRO Admin", password: "1234" },
                { email: "sarah@chemtech-cro.com", label: "Dr. Sarah Chen", role: "CRO Scientist", password: "1234" },
                { email: "rajesh@specialtychem.in", label: "Rajesh Mehta", role: "Manufacturer Admin", password: "1234" },
              ].map((u) => (
                <button
                  key={u.email}
                  type="button"
                  onClick={() => { setEmail(u.email); setPassword(u.password); setError("") }}
                  className="w-full flex items-center justify-between rounded-md px-3 py-2 text-left text-xs hover:bg-muted transition-colors border border-transparent hover:border-border"
                >
                  <div>
                    <p className="font-medium text-foreground">{u.label}</p>
                    <p className="text-muted-foreground">{u.email}</p>
                  </div>
                  <span className="rounded-full bg-background border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {u.role}
                  </span>
                </button>
              ))}
            </div>
          </div>}
    </AuthShell>
  )
}
